import { TableV2MetricValue } from '@/features/shared/components/tableSystem/TableV2MetricChip';
import { s } from '@/theme/tokens';
import { Box, Text } from '@/ui';
import { type ThresholdDirection } from './utils';
import type { ReactElement } from 'react';

type ThresholdPreviewChipsProps = {
  values: { red: number; yellowMin: number; yellowMax: number; green: number };
  formatter: (value: number) => string;
  direction: ThresholdDirection;
  warn: number;
  target: number;
  disabled?: boolean;
  align?: 'center' | 'spread' | 'start';
};

export const ThresholdPreviewChips = ({
  values,
  formatter,
  direction,
  warn,
  target,
  disabled = false,
  align = 'center',
}: ThresholdPreviewChipsProps): ReactElement => {
  const _thresholds = { warn, target, direction } as const;
  const basePreviews = [
    { key: 'red', value: values.red },
    { key: 'yellow-min', value: values.yellowMin },
    { key: 'yellow-max', value: values.yellowMax },
    { key: 'green', value: values.green },
  ] as const;
  const previews =
    direction === 'lowerBetter'
      ? [basePreviews[3], basePreviews[1], basePreviews[2], basePreviews[0]]
      : basePreviews;
  const labelByKey: Record<(typeof basePreviews)[number]['key'], string> = {
    red: 'Alert max.',
    'yellow-min': 'Warn min.',
    'yellow-max': 'Warn max.',
    green: 'Target min.',
  };
  const metricToneByKey = {
    red: 'red',
    'yellow-min': 'orange',
    'yellow-max': 'orange',
    green: 'green',
  } as const;
  const textAlign = align === 'start' ? 'left' : 'center';
  const justifyContent = align === 'start' ? 'flex-start' : 'center';
  const gridTemplateColumns = 'repeat(4, minmax(0, 1fr))';

  return (
    <Box sx={{ display: 'grid', gap: s('sm'), mt: align === 'spread' ? s('xs') : undefined }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          gap: s('sm'),
        }}
      >
        {previews.map((preview) => (
          <Text
            key={`${preview.key}-label`}
            role="body"
            color="textSecondary"
            sx={{
              textAlign,
              fontSize: (theme) => theme.typography.subtitle1.fontSize,
              lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
            }}
          >
            {labelByKey[preview.key]}
          </Text>
        ))}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          gap: s('sm'),
        }}
      >
        {previews.map((preview) => (
          <Box key={preview.key} sx={{ display: 'flex', justifyContent }}>
            <TableV2MetricValue
              accentWidth="3px"
              label={formatter(preview.value)}
              tone={disabled ? 'neutral' : metricToneByKey[preview.key]}
              valueColor="textSecondary"
              variant="detail"
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
